import { useState } from "react";
import { useNotification } from "@/context/NotificationContext";
import { KPIThreshold } from "../api/fetchMemberDashboard/types";
import { saveKPIThreshold } from "../api/saveKPIThreshold/saveKPIThresholdApi";
import { deleteKPIThreshold } from "../api/deleteKPIThreshold/deleteKPIThresholdApi";
import { DashboardKPI } from "../types";
import {
  ThresholdScale,
  buildThresholdBar,
  findThresholdScale,
  snapToStep,
} from "../utils/thresholdBar";
import {
  classifyThresholdZone,
  describeBoundaryWording,
  describeWatchRange,
} from "../utils/thresholdZones";
import { formatValue } from "../utils/toDashboardKPI";

/** The two handles, low value first, as the slider orders them. */
type Boundaries = [number, number];

const collectScaleValues = (kpi: DashboardKPI): number[] => [
  ...kpi.chart.flatMap((point) => (point.value === null ? [] : [point.value])),
  ...(kpi.value === null ? [] : [kpi.value]),
  ...(kpi.threshold === null
    ? []
    : [kpi.threshold.watchStartsAt, kpi.threshold.offTargetStartsAt]),
];

/** Saved boundaries as they are; otherwise the handles start a third of the way in from each end. */
const findStartingBoundaries = (
  kpi: DashboardKPI,
  scale: ThresholdScale,
): Boundaries => {
  if (kpi.threshold !== null) {
    const { watchStartsAt, offTargetStartsAt } = kpi.threshold;

    return [
      Math.min(watchStartsAt, offTargetStartsAt),
      Math.max(watchStartsAt, offTargetStartsAt),
    ];
  }

  const third = (scale.max - scale.min) / 3;

  return [
    snapToStep(scale.min + third, scale.step),
    snapToStep(scale.max - third, scale.step),
  ];
};

const describeLatestValue = (kpi: DashboardKPI): string | null => {
  if (kpi.valueLabel === null) {
    return null;
  }

  if (kpi.monthLabel === null) {
    return kpi.valueLabel;
  }

  return `${kpi.valueLabel} (${kpi.monthLabel})`;
};

/**
 * The thresholds form for one KPI. Mounted only while the modal is open, so
 * every opening starts from the saved values and Cancel discards edits.
 *
 * The empty `catch`es are rejection hygiene, not error handling: the axios
 * interceptor has already toasted the failure, and the modal stays open with
 * where the coach left the handles.
 */
export const useKPIThresholdModal = (
  kpi: DashboardKPI,
  onChange: (threshold: KPIThreshold | null) => void,
) => {
  const { showNotification } = useNotification();
  const [scale] = useState(() =>
    findThresholdScale(collectScaleValues(kpi), kpi.unit),
  );
  const [boundaries, setBoundaries] = useState<Boundaries>(() =>
    findStartingBoundaries(kpi, scale),
  );
  const [isBusy, setIsBusy] = useState(false);

  const isLowerBetter = kpi.direction === "lower_is_better";
  const [lowBoundary, highBoundary] = boundaries;
  const watchStartsAt = isLowerBetter ? lowBoundary : highBoundary;
  const offTargetStartsAt = isLowerBetter ? highBoundary : lowBoundary;

  const boundaryWording = describeBoundaryWording(kpi.direction);
  const onTargetHandleLabel = `On target ${boundaryWording.onTarget}`;
  const offTargetHandleLabel = `Off target ${boundaryWording.offTarget}`;

  const handleBoundariesChange = (values: number[]) =>
    setBoundaries([values[0], values[1]]);

  const save = () => {
    setIsBusy(true);

    saveKPIThreshold(kpi.code, { watchStartsAt, offTargetStartsAt })
      .then((response) => {
        showNotification(
          "Thresholds saved",
          `${kpi.name} thresholds were updated.`,
          "success",
        );
        onChange(response.data);
      })
      .catch(() => undefined)
      .finally(() => setIsBusy(false));
  };

  const clear = () => {
    setIsBusy(true);

    deleteKPIThreshold(kpi.code)
      .then(() => {
        showNotification(
          "Thresholds cleared",
          `${kpi.name} no longer has thresholds.`,
          "success",
        );
        onChange(null);
      })
      .catch(() => undefined)
      .finally(() => setIsBusy(false));
  };

  return {
    scale,
    boundaries,
    handleBoundariesChange,
    handleLabels: isLowerBetter
      ? [onTargetHandleLabel, offTargetHandleLabel]
      : [offTargetHandleLabel, onTargetHandleLabel],
    boundaryWording,
    onTargetValueLabel: formatValue(watchStartsAt, kpi.unit),
    offTargetValueLabel: formatValue(offTargetStartsAt, kpi.unit),
    watchRange: describeWatchRange(
      watchStartsAt,
      offTargetStartsAt,
      kpi.unit,
      kpi.direction,
    ),
    bar: buildThresholdBar(
      watchStartsAt,
      offTargetStartsAt,
      kpi.unit,
      kpi.direction,
      kpi.value,
      scale,
    ),
    latestValueLabel: describeLatestValue(kpi),
    latestValueZone:
      kpi.value === null
        ? null
        : classifyThresholdZone(
            kpi.value,
            watchStartsAt,
            offTargetStartsAt,
            kpi.direction,
          ),
    canSave: !isBusy,
    canClear: kpi.threshold !== null && !isBusy,
    isBusy,
    save,
    clear,
  };
};
